import React from "react"
import { Provider } from "../../types/Providers"
import { NoResultDisplay } from "./NoResultDisplay"
import { ResultList } from "./ResultList"
import { useUserContext } from "../../context/UserContext"
import { useMapViewContext } from "../../context/MapViewContext"
import { MapView } from "../map-view/MapView"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { filterProviderByBenefitConfig } from "../../utils/parentIdMapping"
import { CARROT_CLINIC_PROVIDER_UUID } from "./CarrotClinicCard"
import { Helmet } from "react-helmet"
import { useIntl } from "react-intl"

export function ResultDisplay({
  providers,
  locationCountryCode,
  benefit
}: {
  providers: Provider[]
  locationCountryCode: string
  benefit: Benefit
}): JSX.Element {
  const intl = useIntl()
  const { companyCountryCode } = useUserContext()
  const { showMapView } = useMapViewContext()
  const isCountryCodeMatching = !locationCountryCode || locationCountryCode === companyCountryCode

  // The Carrot Clinic sentinel row (MEM-762) is already gated server-side; the
  // FE benefit filter reads a different set of toggles (PARENT_ID_MAPPING),
  // so let the sentinel through unconditionally to prevent the two gates from
  // diverging.
  const benefitFilter = filterProviderByBenefitConfig(benefit)
  const filteredProviders =
    providers?.filter((p) => p.providerUuid === CARROT_CLINIC_PROVIDER_UUID || benefitFilter(p)) ?? []

  return (
    <>
      {filteredProviders?.length && isCountryCodeMatching ? (
        showMapView ? (
          // MapView plots markers by location and would silently drop the
          // sentinel's null-location row anyway. Strip it here so the map
          // gets a clean provider set; the hero card is a list-view treatment.
          <MapView
            providers={filteredProviders.filter((p) => p.providerUuid !== CARROT_CLINIC_PROVIDER_UUID) as Provider[]}
            benefit={benefit}
          />
        ) : (
          <ResultList providers={filteredProviders as Provider[]} benefit={benefit} />
        )
      ) : (
        <>
          <Helmet>
            <title>
              {intl.formatMessage({
                defaultMessage: "No providers found | Find a provider",
                description: "The title of the current page as shown in the browser's tab or window."
              })}
            </title>
          </Helmet>
          <NoResultDisplay isCountryCodeMatching={isCountryCodeMatching} />
        </>
      )}
    </>
  )
}
